import React, { createContext, useContext, useEffect, useState } from "react";
import { ConfigProvider, theme as antdTheme } from "antd";
import koKR from "antd/locale/ko_KR";

export type ThemeMode = "light" | "dark";

interface ThemeCtxValue {
  mode: ThemeMode;
  toggle: () => void;
}

const ThemeCtx = createContext<ThemeCtxValue>({ mode: "light", toggle: () => {} });

export const useThemeMode = (): ThemeCtxValue => useContext(ThemeCtx);

const STORAGE_KEY = "knollo-console-theme";

const getInitialMode = (): ThemeMode => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

/**
 * 콘솔 전역 테마 — 라이트/다크 토글 + 시스템 기본값 + localStorage 저장.
 *
 * <p>antd 컴포넌트는 algorithm(default/dark) 으로, 커스텀 크롬(사이드바·레이아웃)은
 * data-theme 속성에 반응하는 CSS 변수(index.css)로 동기화된다.</p>
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);
  const isDark = mode === "dark";

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const toggle = () => setMode((m) => (m === "dark" ? "light" : "dark"));

  return (
    <ThemeCtx.Provider value={{ mode, toggle }}>
      <ConfigProvider
        locale={koKR}
        theme={{
          algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
          token: {
            colorPrimary: isDark ? "#F5A623" : "#E8920A",
            colorInfo: isDark ? "#F5A623" : "#E8920A",
            colorLink: isDark ? "#F5A623" : "#C77D0A",
            borderRadius: 8,
            fontFamily: FONT,
          },
          components: {
            Layout: { bodyBg: isDark ? "#0B0B0D" : "#F7F7F8" },
            Table: {
              headerBg: isDark ? "#1C1C20" : "#FAF4E8",
              headerColor: isDark ? "#D4D4D8" : "#6B5836",
            },
          },
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeCtx.Provider>
  );
};
